import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Grid, Typography, Avatar, Chip, IconButton, Tooltip,
  Tabs, Tab, TextField, MenuItem, FormControl, FormLabel,
  RadioGroup, FormControlLabel, Radio, Checkbox, FormGroup,
  Divider, Button, Paper, CircularProgress, Card, CardContent,
  CardActions, ToggleButton, ToggleButtonGroup, Accordion,
  AccordionSummary, AccordionDetails, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle,
} from '@mui/material';
import {
  Person as PersonIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PhotoCamera as PhotoCameraIcon,
  AttachFile as AttachFileIcon,
  ExpandMore as ExpandMoreIcon,
  GridView as GridViewIcon,
  FormatListBulleted as TableListIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Favorite as HealthIcon,
  FamilyRestroom as FamilyIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  ContactPhone as ContactPhoneIcon,
  ArrowBack as ArrowBackIcon,
  Update as UpdateIcon,
  Business as BusinessIcon,
  Link as LinkIcon,
  ThumbUp as AcceptIcon,
  ThumbDown as RefuseIcon,
  Badge as BadgeIcon,
  QrCode as QrCodeIcon,
} from '@mui/icons-material';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { blobticService } from '../../Commun/services/blobticService';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { PublicPreRegistrationModal } from '../components/PublicPreRegistrationModal';
import { StudentCardModal } from '../components/StudentCardModal';
import { academyStore } from '../services/academyStore';
import {
  Student, StudentCategory, StudentGender, BloodGroup,
  GuardianRelation, HealthCondition, SchoolLevel,
} from '../types/academy';

// ─── Constants ───────────────────────────────────────────────────────────────

const SCHOOL_LEVELS: SchoolLevel[] = [
  'primary_1', 'primary_2', 'primary_3', 'primary_4', 'primary_5',
  'middle_1', 'middle_2', 'middle_3', 'middle_4',
];
const BLOOD_GROUPS: BloodGroup[] = ['A-', 'A+', 'B-', 'B+', 'AB-', 'AB+', 'O-', 'O+'];
const GUARDIAN_RELATIONS: GuardianRelation[] = [
  'parent', 'sibling', 'grandparent', 'uncle_aunt', 'guardian', 'other',
];
const HEALTH_CONDITIONS: HealthCondition[] = ['asthma', 'diabetes', 'epilepsy', 'other'];

// DB value → i18n key maps (stored value is English; label is always translated)
const HEALTH_CONDITION_KEYS: Record<HealthCondition, string> = {
  'asthma': 'academy.students.form.health_condition.asthma',
  'diabetes': 'academy.students.form.health_condition.diabetes',
  'epilepsy': 'academy.students.form.health_condition.epilepsy',
  'other': 'academy.students.form.health_condition.other',
};
const GUARDIAN_RELATION_KEYS: Record<GuardianRelation, string> = {
  'parent': 'academy.students.form.guardian_relation.parent',
  'sibling': 'academy.students.form.guardian_relation.sibling',
  'grandparent': 'academy.students.form.guardian_relation.grandparent',
  'uncle_aunt': 'academy.students.form.guardian_relation.uncle_aunt',
  'guardian': 'academy.students.form.guardian_relation.guardian',
  'other': 'academy.students.form.guardian_relation.other',
};
const SCHOOL_LEVEL_KEYS: Record<SchoolLevel, string> = {
  'primary_1': 'academy.students.form.school_level.primary_1',
  'primary_2': 'academy.students.form.school_level.primary_2',
  'primary_3': 'academy.students.form.school_level.primary_3',
  'primary_4': 'academy.students.form.school_level.primary_4',
  'primary_5': 'academy.students.form.school_level.primary_5',
  'middle_1': 'academy.students.form.school_level.middle_1',
  'middle_2': 'academy.students.form.school_level.middle_2',
  'middle_3': 'academy.students.form.school_level.middle_3',
  'middle_4': 'academy.students.form.school_level.middle_4',
};


// entityKey passed to GenericPage crudConfig.
// GenericPage builds: OKey = `${entityKey},${Insert|Update|Delete}${suffix}`
// Backend expects: Blobtic_Academy,academy_student,Insert  (no suffix)
const ENTITY_KEY = 'Blobtic_Academy,academy_student';
const ACTION_SUFFIX = '';            // no suffix → Insert / Update / Delete
const PRIMARY_KEY = 'STD_IDF';    // matches TABLE_IDF in Academy_Student_DM

// ─── DTO ↔ Student mappers ────────────────────────────────────────────────────

/** Map DB row (STD_* columns) → frontend Student object */
const dtoToStudent = (dto: any): Student => ({
  id: dto.STD_IDF,
  uid: dto.STD_UIDF || dto.UIDF || (dto.STD_IDF != null ? String(dto.STD_IDF) : ''),
  STD_UIDF: dto.STD_UIDF || dto.UIDF || (dto.STD_IDF != null ? String(dto.STD_IDF) : ''),
  code: dto.STD_CODE || (dto.STD_IDF != null ? `STD-${String(dto.STD_IDF).padStart(4, '0')}` : ''),
  STD_CODE: dto.STD_CODE || (dto.STD_IDF != null ? `STD-${String(dto.STD_IDF).padStart(4, '0')}` : ''),
  category: dto.STD_CATEGORY || 'adult',
  first_name: dto.STD_FIRST_NAME || '',
  last_name: dto.STD_LAST_NAME || '',
  gender: dto.STD_GENDER,
  date_of_birth: dto.STD_DATE_OF_BIRTH
    ? new Date(dto.STD_DATE_OF_BIRTH).toISOString().split('T')[0]
    : '',
  address: dto.STD_ADDRESS || '',
  email: dto.STD_EMAIL || '',
  phone: dto.STD_PHONE || '',
  emergency_contact: dto.STD_EMERGENCY_CONTACT || '',
  financial_hold: Boolean(dto.STD_FINANCIAL_HOLD),
  hold_since: dto.STD_HOLD_SINCE
    ? new Date(dto.STD_HOLD_SINCE).toISOString().split('T')[0]
    : undefined,
  profession: dto.STD_PROFESSION || '',
  school_level: dto.STD_SCHOOL_LEVEL,
  has_health_issues: Boolean(dto.STD_HAS_HEALTH_ISSUES),
  health_conditions: dto.STD_HEALTH_CONDITIONS
    ? (typeof dto.STD_HEALTH_CONDITIONS === 'string'
      ? dto.STD_HEALTH_CONDITIONS.split(',').map((s: string) => s.trim()).filter(Boolean)
      : dto.STD_HEALTH_CONDITIONS)
    : [],
  health_other_detail: dto.STD_HEALTH_OTHER_DETAIL || '',
  has_allergies: Boolean(dto.STD_HAS_ALLERGIES),
  allergy_details: dto.STD_ALLERGY_DETAILS || '',
  blood_group: dto.STD_BLOOD_GROUP || undefined,
  guardian_first_name: dto.STD_GUARDIAN_FIRST_NAME || '',
  guardian_last_name: dto.STD_GUARDIAN_LAST_NAME || '',
  guardian_relation: dto.STD_GUARDIAN_RELATION || undefined,
  guardian_phone: dto.STD_GUARDIAN_PHONE || '',
  guardian_email: dto.STD_GUARDIAN_EMAIL || '',
  authorized_pickup: dto.STD_AUTHORIZED_PICKUP || '',
  image_right: dto.STD_IMAGE_RIGHT == null ? true : Boolean(dto.STD_IMAGE_RIGHT),
  photo_base64: dto.STD_PHOTO_BASE64,
  photo_mime_type: dto.STD_PHOTO_MIME_TYPE,
  attached_file_name: dto.STD_FILE_NAME,
  attached_file_base64: dto.STD_FILE_BASE64,
  attached_file_mime_type: dto.STD_FILE_MIME_TYPE,
  registration_status: dto.STD_REGISTRATION_STATUS || 'Active',
  crs_idf: dto.CRS_IDF ? Number(dto.CRS_IDF) : undefined,
  crs_title: dto.CRS_TITLE || undefined,
  time_preference: dto.STD_TIME_PREFERENCE || undefined,
  loi_18_07_consent: dto.STD_LOI_18_07_CONSENT != null
    ? Boolean(dto.STD_LOI_18_07_CONSENT)
    : undefined,
  loi_18_07_consent_date: dto.STD_LOI_18_07_CONSENT_DATE
    ? new Date(dto.STD_LOI_18_07_CONSENT_DATE).toLocaleString('fr-DZ')
    : undefined,
  // Discount Affectation
  discount_id: dto.DSC_IDF ? Number(dto.DSC_IDF) : undefined,
  discount_code: dto.DSC_CODE || undefined,
  discount_name: dto.DSC_NAME || undefined,
  // Dossier (from LEFT JOIN)
  dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
  dos_code: dto.DOS_CODE || undefined,
  dos_lib: dto.DOS_LIB || undefined,
  // Audit (from LEFT JOIN)
  user_lib: dto.USER_LIB || undefined,
  last_updt: dto.LAST_UPDT
    ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ')
    : undefined,
  portal_active: dto.STD_PORTAL_ACTIVE != null ? Boolean(Number(dto.STD_PORTAL_ACTIVE)) : false,
  portal_password: dto.STD_PORTAL_PASSWORD || '',
});

/** Map frontend Student object → DB column names expected by Academy_Student_DM */
const studentToDto = (s: Partial<Student>): Record<string, any> => ({
  STD_IDF: (s as any).STD_IDF ?? s.id,          // keep existing PK for updates
  STD_CATEGORY: s.category ?? 'adult',
  STD_FIRST_NAME: s.first_name,
  STD_LAST_NAME: s.last_name,
  STD_GENDER: s.gender,
  STD_DATE_OF_BIRTH: s.date_of_birth || null,
  STD_ADDRESS: s.address || null,
  STD_EMAIL: s.email || null,
  STD_PHONE: s.phone || null,
  STD_EMERGENCY_CONTACT: s.emergency_contact || null,
  STD_FINANCIAL_HOLD: s.financial_hold ? 1 : 0,
  STD_HOLD_SINCE: s.hold_since || null,
  STD_PROFESSION: s.profession || null,
  STD_REGISTRATION_STATUS: s.registration_status || 'Active',
  STD_SCHOOL_LEVEL: s.school_level || null,
  STD_HAS_HEALTH_ISSUES: s.has_health_issues ?? null,   // send true/false/null (not 1/0)
  STD_HEALTH_CONDITIONS: Array.isArray(s.health_conditions) && s.health_conditions.length
    ? s.health_conditions.join(',') : null,
  STD_HEALTH_OTHER_DETAIL: s.health_other_detail || null,
  STD_HAS_ALLERGIES: s.has_allergies ?? null,            // send true/false/null
  STD_ALLERGY_DETAILS: s.allergy_details || null,
  STD_BLOOD_GROUP: s.blood_group || null,
  STD_GUARDIAN_FIRST_NAME: s.guardian_first_name || null,
  STD_GUARDIAN_LAST_NAME: s.guardian_last_name || null,
  STD_GUARDIAN_RELATION: s.guardian_relation || null,
  STD_GUARDIAN_PHONE: s.guardian_phone || null,
  STD_GUARDIAN_EMAIL: s.guardian_email || null,
  STD_AUTHORIZED_PICKUP: s.authorized_pickup || null,
  STD_IMAGE_RIGHT: s.image_right ?? null,                // send true/false/null
  STD_PHOTO_BASE64: s.photo_base64 || null,
  STD_PHOTO_MIME_TYPE: s.photo_mime_type || null,
  STD_FILE_NAME: s.attached_file_name || null,
  STD_FILE_BASE64: s.attached_file_base64 || null,
  STD_FILE_MIME_TYPE: s.attached_file_mime_type || null,
  DSC_IDF: s.discount_id || null,
  STD_PORTAL_ACTIVE: s.portal_active ? 1 : 0,
  STD_PORTAL_PASSWORD: s.portal_password || '',
  // DOS_IDF is auto-set by backend from session; send only if already known (edit mode)
  ...(s.dos_idf ? { DOS_IDF: s.dos_idf } : {}),
});

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const emptyStudent = (): Partial<Student> => ({
  category: 'adult',
  first_name: '',
  last_name: '',
  gender: undefined,
  date_of_birth: '',
  address: '',
  email: '',
  phone: '',
  emergency_contact: '',
  profession: '',
  school_level: undefined,
  has_health_issues: false,
  health_conditions: [],
  health_other_detail: '',
  has_allergies: false,
  allergy_details: '',
  blood_group: undefined,
  guardian_first_name: '',
  guardian_last_name: '',
  guardian_relation: undefined,
  guardian_phone: '',
  authorized_pickup: '',
  image_right: true,
  photo_base64: undefined,
  photo_mime_type: undefined,
  attached_file_name: undefined,
  attached_file_base64: undefined,
  attached_file_mime_type: undefined,
  portal_active: false,
  portal_password: '',
});

// ─── Main Component ───────────────────────────────────────────────────────────

export const StudentsPage: React.FC = () => {
  const { t } = useLanguage();


  // View mode: 'list' (default) or 'form' (new/edit)
  const [view, setView] = useState<'list' | 'form'>('list');

  // Grid vs Table toggle for the list (initialized from user default preference)
  const [viewMode, setViewMode] = useDefaultViewMode();

  // Student list from academyStore
  const [students, setStudents] = useState<Student[]>(() => academyStore.getStudents());
  const [listLoading, setListLoading] = useState(false);

  // Financial snapshot: STD_IDF → { invoiced, paid, rest }
  const [invoiceMap, setInvoiceMap] = useState<Record<number, { invoiced: number; paid: number; rest: number }>>({});

  // Form data (used both for creating and editing)
  const [formData, setFormData] = useState<Partial<Student>>(emptyStudent());

  // Adult (0) / Junior (1) tab
  const [categoryTab, setCategoryTab] = useState<0 | 1>(0);

  // Public link generator modal
  const [publicModalOpen, setPublicModalOpen] = useState(false);

  // Student Badge Print Card modal
  const [selectedCardStudent, setSelectedCardStudent] = useState<Student | null>(null);
  const [cardModalOpen, setCardModalOpen] = useState(false);

  const handleOpenStudentCard = (student: Student) => {
    setSelectedCardStudent(student);
    setCardModalOpen(true);
  };

  // File input refs
  const photoInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Confirmation dialog state (generic confirm modal)
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    content: string;
    color?: 'primary' | 'error' | 'warning' | 'success';
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    content: '',
    onConfirm: () => { },
  });

  const handleAcceptPreRegistration = (student: Student) => {
    setConfirmDialog({
      open: true,
      title: t('academy.students.confirm_accept_title'),
      content: t('academy.students.confirm_accept_msg', student.first_name, student.last_name),
      color: 'success',
      onConfirm: async () => {
        try {
          await academyStore.updateStudent({ ...student, registration_status: 'Active' });
          blobticMessage.success(
            t('academy.students.success_accept_title'),
            t('academy.students.success_accept_msg', student.first_name, student.last_name)
          );
        } catch (err: any) {
          blobticMessage.error(t('common.error'), err.message || t('academy.students.error_accept'));
        }
      },
    });
  };

  const handleRefusePreRegistration = (student: Student) => {
    setConfirmDialog({
      open: true,
      title: t('academy.students.confirm_refuse_title'),
      content: t('academy.students.confirm_refuse_msg', student.first_name, student.last_name),
      color: 'error',
      onConfirm: async () => {
        try {
          await academyStore.updateStudent({ ...student, registration_status: 'Refused' });
          blobticMessage.success(
            t('academy.students.success_refuse_title'),
            t('academy.students.success_refuse_msg', student.first_name, student.last_name)
          );
        } catch (err: any) {
          blobticMessage.error(t('common.error'), err.message || t('academy.students.error_refuse'));
        }
      },
    });
  };

  // Sync with academyStore
  useEffect(() => {
    academyStore.ensureStudentsLoaded();
    const syncWithStore = () => {
      setStudents(academyStore.getStudents());
      const invs = academyStore.getInvoices();
      const map: Record<number, { invoiced: number; paid: number; rest: number }> = {};
      for (const inv of invs) {
        const sid = Number(inv.student_id);
        if (!sid) continue;
        if (!map[sid]) map[sid] = { invoiced: 0, paid: 0, rest: 0 };
        const total = Number(inv.total_amount || 0);
        const paid = Number(inv.amount_paid || 0);
        map[sid].invoiced += total;
        map[sid].paid += paid;
        map[sid].rest += (total - paid);
      }
      setInvoiceMap(map);
    };
    syncWithStore();
    return academyStore.subscribe(syncWithStore);
  }, []);

  // ── Navigation helpers ──────────────────────────────────────────────────────

  const openNew = () => {
    setFormData(emptyStudent());
    setCategoryTab(0);
    setView('form');
  };

  const openEdit = (student: Student) => {
    setFormData({
      ...student,
      has_health_issues: Boolean(student.has_health_issues),
      has_allergies: Boolean(student.has_allergies),
      image_right: student.image_right == null ? true : Boolean(student.image_right),
    });
    setCategoryTab(student.category === 'junior' ? 1 : 0);
    setView('form');
  };

  const backToList = () => {
    setView('list');
    setFormData(emptyStudent());
  };

  // ── Form field handler ──────────────────────────────────────────────────────

  const set = <K extends keyof Student>(key: K, value: Student[K]) =>
    setFormData(prev => ({ ...prev, [key]: value }));

  const handleTabChange = (_: React.SyntheticEvent, val: 0 | 1) => {
    setCategoryTab(val);
    set('category', val === 1 ? 'junior' : 'adult');
  };

  // ── Photo / file ─────────────────────────────────────────────────────────

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const b64 = await fileToBase64(file);
    setFormData(prev => ({ ...prev, photo_base64: b64, photo_mime_type: file.type }));
    e.target.value = '';
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const b64 = await fileToBase64(file);
    setFormData(prev => ({
      ...prev,
      attached_file_name: file.name,
      attached_file_base64: b64,
      attached_file_mime_type: file.type,
    }));
    e.target.value = '';
  };

  const toggleHealthCondition = (cond: HealthCondition) => {
    const current = formData.health_conditions ?? [];
    set('health_conditions', current.includes(cond)
      ? current.filter(c => c !== cond)
      : [...current, cond]);
  };

  // ── GenericPage callbacks (used in form view) ──────────────────────────────

  const handleValidate = (): string | null => {
    if (!formData.first_name?.trim()) return t('academy.students.validation.first_name_required');
    if (!formData.last_name?.trim()) return t('academy.students.validation.last_name_required');
    if (!formData.gender) return t('academy.students.validation.gender_required');
    if (!formData.date_of_birth) return t('academy.students.validation.dob_required');
    if (formData.category === 'junior') {
      if (formData.has_health_issues && (formData.health_conditions ?? []).length === 0 && !formData.health_other_detail?.trim()) {
        return t('academy.students.validation.health_issues_required');
      }
      if (formData.has_allergies && !formData.allergy_details?.trim()) {
        return t('academy.students.validation.allergies_required');
      }
      if (!formData.guardian_first_name?.trim()) return t('academy.students.validation.guardian_first_name_required');
      if (!formData.guardian_last_name?.trim()) return t('academy.students.validation.guardian_last_name_required');
      if (!formData.guardian_relation) return t('academy.students.validation.guardian_relation_required');
      if (!formData.guardian_phone?.trim()) return t('academy.students.validation.guardian_phone_required');
    }
    return null;
  };

  // GenericPage auto-updates IndexedDB → academyStore refreshes via subscription.
  const handleDataChange = () => backToList();

  const handleClear = () => backToList();


  // ── Edit & Delete (from list/grid/table) ───────────────────────────────────

  const handleEditFromList = (student: Student) => {
    setConfirmDialog({
      open: true,
      title: t('common.confirm.title.update') || 'Confirmer la modification',
      content: t('academy.students.confirm_edit', student.first_name, student.last_name),
      color: 'primary',
      onConfirm: () => openEdit(student),
    });
  };

  const handleDeleteFromList = (student: Student) => {
    setConfirmDialog({
      open: true,
      title: t('common.confirm.title.delete') || 'Confirmer la suppression',
      content: t('academy.students.confirm_delete', student.first_name, student.last_name),
      color: 'error',
      onConfirm: async () => {
        const res = await academyStore.deleteStudent(student.id);
        if (res.success) {
          blobticMessage.success(t('common.messages.success'), t('common.messages.deleted_success'));
        } else {
          blobticMessage.error(t('common.error'), res.message || 'Erreur lors de la suppression.');
        }
      },
    });
  };

  // ── Shared field sx ────────────────────────────────────────────────────────

  const fsx = { mb: 0 };

  // ─── LIST VIEW ─────────────────────────────────────────────────────────────

  if (view === 'list') {
    return (
      <GenericPage
        title={t('academy.students.title')}
        icon={<PersonIcon />}
        footerEnabled={false}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onNew={openNew}
        newButtonLabel={t('academy.students.new_student')}
        additionalActions={
          <Button
            variant="outlined"
            color="primary"
            startIcon={<LinkIcon />}
            onClick={() => setPublicModalOpen(true)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {t('academy.students.prereg_link')}
          </Button>
        }
      >
        {/* ── Grid view ── */}
        {viewMode === 'grid' && (
          listLoading ? (
            <Box display="flex" justifyContent="center" p={6}><CircularProgress /></Box>
          ) : students.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" p={6} gap={2}>
              <PersonIcon sx={{ fontSize: 64, color: 'action.disabled' }} />
              <Typography color="text.secondary">{t('academy.students.no_students')}</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>{t('academy.students.new_student')}</Button>
            </Box>
          ) : (
            <Grid container spacing={2.5}>
              {students.map(student => {
                const snap = invoiceMap[student.id] ?? { invoiced: 0, paid: 0, rest: 0 };
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={student.id}>
                    <Card variant="outlined" sx={{
                      borderRadius: 3,
                      borderColor: student.financial_hold ? 'error.light' : 'divider',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
                    }}>
                      <CardContent sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1.5, gap: 1 }}>
                          <Avatar src={student.photo_base64}
                            sx={{ width: 72, height: 72, bgcolor: student.financial_hold ? 'error.main' : 'primary.main', fontSize: '1.5rem', border: '3px solid', borderColor: student.financial_hold ? 'error.light' : 'primary.light' }}>
                            {!student.photo_base64 && `${student.first_name?.[0] ?? ''}${student.last_name?.[0] ?? ''}`}
                          </Avatar>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>{student.first_name} {student.last_name}</Typography>
                            <Typography variant="caption" color="text.secondary">{student.date_of_birth || '—'}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          <Chip label={student.category === 'junior' ? `${t('academy.students.form.junior')}` : `${t('academy.students.form.adult')}`} size="small" variant="outlined" color="primary" />
                          {student.registration_status === 'Pre-Registered' && (
                            <Chip label={t('academy.students.status.preregistered')} size="small" color="warning" icon={<WarningIcon />} />
                          )}
                          {student.registration_status === 'Refused' && (
                            <Chip label={t('academy.students.status.refused')} size="small" color="error" variant="outlined" />
                          )}
                          {student.financial_hold
                            ? <Chip label={t('academy.students.hold_badge')} size="small" color="error" icon={<WarningIcon />} />
                            : <Chip label={t('academy.students.active_badge')} size="small" color="success" variant="outlined" icon={<CheckCircleIcon />} />}
                          {student.portal_active && (
                            <Chip label={t('academy.students.portal_badge_active')} size="small" color="info" variant="outlined" />
                          )}
                        </Box>
                        {/* Financial Snapshot mini */}
                        <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1, py: 0.5, mt: 1 }}>
                          <Typography variant="caption" display="block">{t('academy.students.card.invoiced')}: <b>DZD{snap.invoiced}</b></Typography>
                          <Typography variant="caption" display="block">{t('academy.students.card.paid')}: <b>DZD{snap.paid}</b></Typography>
                          <Typography variant="caption" display="block" color={snap.rest > 0 ? 'error.main' : 'success.main'} fontWeight="bold">
                            {t('academy.students.card.rest')}: DZD{snap.rest}
                          </Typography>
                        </Box>
                      </CardContent>
                      <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                        <Tooltip title={t('academy.students.print_card') || 'Student Badge'}>
                          <IconButton size="small" color="secondary" onClick={() => handleOpenStudentCard(student)}>
                            <BadgeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.edit')}><IconButton size="small" color="primary" onClick={() => handleEditFromList(student)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title={t('common.delete')}><IconButton size="small" color="error" onClick={() => handleDeleteFromList(student)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )
        )}

        {/* ── Rich Table view — GenericTable ── */}
        {viewMode === 'table' && (
          listLoading ? (
            <Box display="flex" justifyContent="center" p={6}><CircularProgress /></Box>
          ) : (
            <GenericTable
              columns={[
                {
                  id: 'name',
                  label: t('academy.students.table.name'),
                  minWidth: 180,
                  format: (_val, row) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        src={row.photo_base64}
                        sx={{
                          width: 38, height: 38,
                          bgcolor: row.financial_hold ? 'error.main' : 'primary.main',
                          fontSize: '0.9rem', fontWeight: 700,
                        }}
                      >
                        {!row.photo_base64 && `${row.first_name?.[0] ?? ''}${row.last_name?.[0] ?? ''}`}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={700}>{row.first_name} {row.last_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{t('academy.students.table.dob_prefix')}: {row.date_of_birth || '—'}</Typography>
                      </Box>
                    </Box>
                  ),
                },
                {
                  id: 'category',
                  label: t('academy.students.form.category'),
                  minWidth: 90,
                  format: (val) => (
                    <Chip
                      label={val === 'junior' ? `${t('academy.students.form.junior')}` : `${t('academy.students.form.adult')}`}
                      size="small" variant="outlined" color="primary"
                    />
                  ),
                },
                {
                  id: 'email',
                  label: t('academy.students.table.contact'),
                  minWidth: 160,
                  format: (_val, row) => (
                    <Box>
                      <Typography variant="body2" sx={{ color: 'primary.main', fontSize: '0.8rem' }}>{row.email || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.phone || '—'}</Typography>
                    </Box>
                  ),
                },
                {
                  id: 'emergency_contact',
                  label: t('academy.students.table.emergency'),
                  minWidth: 150,
                  format: (val) => (
                    <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 180, display: 'block' }}>
                      {val || '—'}
                    </Typography>
                  ),
                },
                {
                  id: 'portal_active',
                  label: t('academy.students.portal_section'),
                  minWidth: 120,
                  format: (val) => (
                    <Chip
                      label={val ? t('academy.students.portal_badge_active') : t('academy.students.portal_badge_inactive')}
                      size="small"
                      color={val ? 'info' : 'default'}
                      variant={val ? 'filled' : 'outlined'}
                    />
                  ),
                },
                {
                  id: 'financial_snapshot',
                  label: t('academy.students.financial_snapshot'),
                  minWidth: 160,
                  format: (_val, row) => {
                    const snap = invoiceMap[row.id] ?? { invoiced: 0, paid: 0, rest: 0 };
                    return (
                      <Box>
                        <Typography variant="caption" display="block" color="text.secondary">{t('academy.students.table.invoiced')}: DZD{snap.invoiced.toLocaleString()}</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">{t('academy.students.table.total_paid')}: <span style={{ color: '#2e7d32' }}>DZD{snap.paid.toLocaleString()}</span></Typography>
                        <Typography variant="caption" display="block" fontWeight={700} sx={{ color: snap.rest > 0 ? 'error.main' : 'success.main' }}>
                          {t('academy.students.table.rest_to_pay')}: DZD{snap.rest.toLocaleString()}
                        </Typography>
                      </Box>
                    );
                  },
                },
                {
                  id: 'financial_hold',
                  label: t('academy.students.table.status'),
                  minWidth: 110,
                  format: (val) => val
                    ? <Chip label={t('academy.students.hold_badge')} color="error" size="small" icon={<WarningIcon />} />
                    : <Chip label={t('academy.students.active_badge')} color="success" size="small" variant="outlined" icon={<CheckCircleIcon />} />,
                },
                {
                  id: 'registration_status',
                  label: t('academy.students.table.registration_status'),
                  minWidth: 170,
                  format: (val, row) => {
                    if (val === 'Pre-Registered') {
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip label={t('academy.students.status.preregistered')} color="warning" size="small" icon={<WarningIcon />} />
                          <Tooltip title={t('academy.students.action_accept')}>
                            <IconButton size="small" color="success" onClick={() => handleAcceptPreRegistration(row)}>
                              <AcceptIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('academy.students.action_refuse')}>
                            <IconButton size="small" color="error" onClick={() => handleRefusePreRegistration(row)}>
                              <RefuseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      );
                    }
                    if (val === 'Refused') {
                      return <Chip label={t('academy.students.status.refused')} color="error" size="small" variant="outlined" />;
                    }
                    return <Chip label={t('academy.students.status.active')} color="success" size="small" variant="outlined" icon={<CheckCircleIcon />} />;
                  },
                },
              ]}
              data={students.map(s => ({
                ...s,
                name: `${s.first_name} ${s.last_name}`,
                financial_snapshot: null,   // rendered via formatter using invoiceMap
              }))}
              rowActions={(row) => (
                <Tooltip title={t('academy.students.print_card') || 'Student Badge'}>
                  <IconButton size="small" color="secondary" onClick={() => handleOpenStudentCard(students.find(s => s.id === row.id)!)}>
                    <BadgeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              onEdit={(row) => handleEditFromList(students.find(s => s.id === row.id)!)}
              onDelete={(row) => handleDeleteFromList(students.find(s => s.id === row.id)!)}
              filterColumns={['category', 'registration_status', 'financial_hold', 'gender']}
              searchPlaceholder={t('academy.students.search_placeholder')}
              maxHeight="calc(100vh - 380px)"
            />
          )
        )}
        <PublicPreRegistrationModal
          open={publicModalOpen}
          onClose={() => setPublicModalOpen(false)}
        />
        <StudentCardModal
          open={cardModalOpen}
          onClose={() => setCardModalOpen(false)}
          student={selectedCardStudent}
        />
        <Dialog
          open={confirmDialog.open}
          onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
          aria-labelledby="student-confirm-dialog-title"
          aria-describedby="student-confirm-dialog-description"
        >
          <DialogTitle id="student-confirm-dialog-title" sx={{ fontWeight: 600 }}>
            {confirmDialog.title}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="student-confirm-dialog-description">
              {confirmDialog.content}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))} variant="outlined" color="inherit">
              {t('btn.cancel')}
            </Button>
            <Button
              onClick={() => {
                const action = confirmDialog.onConfirm;
                setConfirmDialog(prev => ({ ...prev, open: false }));
                action();
              }}
              autoFocus
              color={confirmDialog.color || 'primary'}
              variant="contained"
            >
              {t('common.actions.confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </GenericPage>
    );
  }

  // ─── FORM VIEW (New / Edit) ────────────────────────────────────────────────

  // GenericPage checks data[PRIMARY_KEY] ('STD_IDF') to decide Insert vs Update.
  // We convert formData (camelCase) to the DTO (STD_* column names) before passing to GenericPage.
  const isEditing = !!(formData as any).STD_IDF || !!formData.id;
  const formPayload = studentToDto(formData);  // { STD_FIRST_NAME, STD_LAST_NAME, ... }

  return (
    <GenericPage
      title={isEditing
        ? t('academy.students.form.title_edit', formData.first_name, formData.last_name)
        : t('academy.students.form.title_new')}
      icon={<PersonIcon />}
      footerEnabled={true}
      footerInfo={{
        lastUpdate: formData.last_updt || '-',
        userLib: formData.user_lib || '-',
      }}
      additionalActions={
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={backToList}
        >
          {t('academy.students.back_to_list')}
        </Button>
      }
      crudConfig={{
        entityKey: ENTITY_KEY,
        actionSuffix: ACTION_SUFFIX,   // '' → Insert / Update / Delete
        primaryKey: PRIMARY_KEY,       // 'STD_IDF'
      }}
      data={formPayload}
      onDataChange={() => handleDataChange()}

      onValidate={handleValidate}
      onClear={handleClear}
    >
      <Box sx={{ maxWidth: 900, mx: 'auto', pb: 4 }}>

        {/* ── Photo + File attachment ── */}
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap', mb: 3 }}>

          {/* Photo */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={formData.photo_base64}
                sx={{
                  width: 100, height: 100,
                  bgcolor: 'primary.light',
                  fontSize: '2.2rem',
                  border: '3px solid',
                  borderColor: 'primary.main',
                }}
              >
                {!formData.photo_base64 && `${formData.first_name?.[0] ?? ''}${formData.last_name?.[0] ?? ''}`}
              </Avatar>
              <Tooltip title="Changer la photo">
                <IconButton
                  size="small"
                  onClick={() => photoInputRef.current?.click()}
                  sx={{
                    position: 'absolute', bottom: -4, right: -4,
                    bgcolor: 'primary.main', color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                    width: 28, height: 28,
                  }}
                >
                  <PhotoCameraIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary">{t('academy.students.form.photo_caption')}</Typography>
            <input ref={photoInputRef} type="file" accept="image/*" hidden onChange={handlePhotoChange} />
          </Box>

          {/* File attachment */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, justifyContent: 'center' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {t('academy.students.form.file_label')}
            </Typography>
            {formData.attached_file_name ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                <Tooltip title={t('academy.students.form.view_file')}>
                  <Chip
                    icon={<AttachFileIcon />}
                    label={formData.attached_file_name}
                    onClick={() => {
                      if (formData.attached_file_base64 && formData.attached_file_mime_type) {
                        // The stored base64 may already include the 'data:mime;base64,' prefix
                        const raw = formData.attached_file_base64.includes(',')
                          ? formData.attached_file_base64.split(',')[1]
                          : formData.attached_file_base64;
                        try {
                          const byteChars = atob(raw);
                          const byteArray = new Uint8Array(byteChars.length);
                          for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
                          const blob = new Blob([byteArray], { type: formData.attached_file_mime_type });
                          const url = URL.createObjectURL(blob);
                          window.open(url, '_blank');
                          setTimeout(() => URL.revokeObjectURL(url), 15000);
                        } catch {
                          // Fallback: use as data URL (already prefixed or build one)
                          const dataUrl = formData.attached_file_base64.startsWith('data:')
                            ? formData.attached_file_base64
                            : `data:${formData.attached_file_mime_type};base64,${formData.attached_file_base64}`;
                          window.open(dataUrl, '_blank');
                        }
                      }
                    }}
                    onDelete={() => setFormData(prev => ({
                      ...prev,
                      attached_file_name: undefined,
                      attached_file_base64: undefined,
                      attached_file_mime_type: undefined,
                    }))}
                    variant="outlined"
                    color="primary"
                    sx={{ maxWidth: 280, cursor: 'pointer', '&:hover': { bgcolor: 'primary.50' } }}
                  />
                </Tooltip>
                {/* Replace file button */}
                <Button
                  size="small"
                  variant="text"
                  startIcon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ textTransform: 'none', fontSize: '0.72rem', color: 'text.secondary', py: 0, px: 0.5 }}
                >
                  {t('academy.students.form.attach_file')}
                </Button>
              </Box>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AttachFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ textTransform: 'none', borderStyle: 'dashed', alignSelf: 'flex-start' }}
              >
                {t('academy.students.form.attach_file')}
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              hidden
              onChange={handleFileChange}
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* ── Category tabs ── */}
        <Tabs
          value={categoryTab}
          onChange={handleTabChange}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
          variant="fullWidth"
        >
          <Tab label={t('academy.students.form.tab_adult')} sx={{ fontWeight: 600 }} />
          <Tab label={t('academy.students.form.tab_junior')} sx={{ fontWeight: 600 }} />
        </Tabs>

        {/* ── Common fields: Personal Information (top of adult & junior tabs) ── */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
            {t('academy.students.form.section_personal')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                required
                label={t('academy.students.form.firstname')} size="small" fullWidth sx={fsx}
                value={formData.first_name ?? ''}
                onChange={e => set('first_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                required
                label={t('academy.students.form.lastname')} size="small" fullWidth sx={fsx}
                value={formData.last_name ?? ''}
                onChange={e => set('last_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label={t('academy.students.form.registration_status')}
                size="small" fullWidth sx={fsx}
                value={formData.registration_status ?? 'Active'}
                onChange={e => set('registration_status', e.target.value)}
              >
                <MenuItem value="Active">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    <span>{t('academy.students.status.active')}</span>
                  </Box>
                </MenuItem>
                <MenuItem value="Pre-Registered">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                    <span>{t('academy.students.status.preregistered')}</span>
                  </Box>
                </MenuItem>
                <MenuItem value="Refused">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    <span>{t('academy.students.status.refused')}</span>
                  </Box>
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                select label={t('academy.students.form.gender')} size="small" fullWidth sx={fsx}
                value={formData.gender ?? ''}
                onChange={e => set('gender', e.target.value as StudentGender)}
              >
                <MenuItem value="male">{t('academy.students.form.gender_male')}</MenuItem>
                <MenuItem value="female">{t('academy.students.form.gender_female')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                label={t('academy.students.form.dob')} type="date" size="small" fullWidth sx={fsx}
                value={formData.date_of_birth ?? ''}
                onChange={e => set('date_of_birth', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('academy.students.form.address')} size="small" fullWidth sx={fsx}
                value={formData.address ?? ''}
                onChange={e => set('address', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        {(formData.registration_status === 'Pre-Registered'
          || formData.crs_idf
          || formData.time_preference
          || formData.loi_18_07_consent != null) && (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                {t('academy.students.form.section_prereg')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t('academy.students.field.course_template')}
                    size="small"
                    fullWidth
                    sx={fsx}
                    value={formData.crs_title || formData.crs_idf?.toString() || '—'}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t('academy.students.field.time_preference')}
                    size="small"
                    fullWidth
                    sx={fsx}
                    value={formData.time_preference || '—'}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t('academy.students.field.loi_consent')}
                    size="small"
                    fullWidth
                    sx={fsx}
                    value={
                      formData.loi_18_07_consent == null
                        ? '—'
                        : formData.loi_18_07_consent
                          ? `Oui${formData.loi_18_07_consent_date ? ` (${formData.loi_18_07_consent_date})` : ''}`
                          : 'Non'
                    }
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>
            </Paper>
          )}

        {/* ── Emergency contact (top of adult & junior tabs) ── */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
            {t('academy.students.form.emergency_contact')}
          </Typography>
          <TextField
            required
            label={t('academy.students.form.emergency_contact')} size="small" fullWidth
            value={formData.emergency_contact ?? ''}
            onChange={e => set('emergency_contact', e.target.value)}
            placeholder="ex : Parent: (+213 550 000 111)"
          />
        </Paper>

        {/* ── Adult-specific ── */}
        {categoryTab === 0 && (
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WorkIcon fontSize="small" /> {t('academy.students.form.section_adult')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('academy.students.form.profession')} size="small" fullWidth sx={fsx}
                  value={formData.profession ?? ''}
                  onChange={e => set('profession', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('academy.students.form.email')} type="email" size="small" fullWidth sx={fsx}
                  value={formData.email ?? ''}
                  onChange={e => set('email', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  label={t('academy.students.form.phone')} size="small" fullWidth sx={fsx}
                  value={formData.phone ?? ''}
                  onChange={e => set('phone', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* ── Junior-specific ── */}
        {categoryTab === 1 && (
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <SchoolIcon fontSize="small" /> {t('academy.students.form.section_junior')}
            </Typography>
            <Grid container spacing={2}>

              {/* School level */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select label={t('academy.students.form.school_level')} size="small" fullWidth sx={fsx}
                  value={formData.school_level ?? ''}
                  onChange={e => set('school_level', e.target.value as SchoolLevel)}
                >
                  {SCHOOL_LEVELS.map(lvl => <MenuItem key={lvl} value={lvl}>{t(SCHOOL_LEVEL_KEYS[lvl])}</MenuItem>)}
                </TextField>
              </Grid>

              {/* Blood group */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select label={t('academy.students.form.blood_group')} size="small" fullWidth sx={fsx}
                  value={formData.blood_group ?? ''}
                  onChange={e => set('blood_group', e.target.value as BloodGroup)}
                >
                  <MenuItem value=""><em>{t('academy.students.form.blood_group_none')}</em></MenuItem>
                  {BLOOD_GROUPS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </TextField>
              </Grid>

              {/* Health issues */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default' }}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <HealthIcon sx={{ fontSize: 16 }} />
                      {t('academy.students.form.health_issues')}
                    </FormLabel>
                    <RadioGroup
                      row
                      value={formData.has_health_issues === true ? 'yes' : formData.has_health_issues === false ? 'no' : ''}
                      onChange={e => set('has_health_issues', e.target.value === 'yes')}
                    >
                      <FormControlLabel value="yes" control={<Radio size="small" />} label={t('academy.students.form.yes')} />
                      <FormControlLabel value="no" control={<Radio size="small" />} label={t('academy.students.form.no')} />
                    </RadioGroup>
                    {formData.has_health_issues && (
                      <>
                        <FormLabel sx={{ fontSize: '0.8rem', mt: 1.5, mb: 0.5, fontWeight: 600 }}>
                          {t('academy.students.form.health_specify')}
                        </FormLabel>
                        <FormGroup row>
                          {HEALTH_CONDITIONS.map(cond => (
                            <FormControlLabel
                              key={cond}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={(formData.health_conditions ?? []).includes(cond)}
                                  onChange={() => toggleHealthCondition(cond)}
                                />
                              }
                              label={t(HEALTH_CONDITION_KEYS[cond])}
                            />
                          ))}
                        </FormGroup>
                        {(formData.health_conditions ?? []).includes('other') && (
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                            sx={{ mt: 1.5 }}
                            label={t('academy.students.form.health_other_detail')}
                            placeholder={t('academy.students.form.health_other_detail_placeholder')}
                            value={formData.health_other_detail ?? ''}
                            onChange={e => set('health_other_detail', e.target.value)}
                          />
                        )}
                      </>
                    )}
                  </FormControl>
                </Paper>
              </Grid>

              {/* Allergies */}
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default' }}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 1 }}>{t('academy.students.form.allergies')}</FormLabel>
                    <RadioGroup
                      row
                      value={formData.has_allergies === true ? 'yes' : formData.has_allergies === false ? 'no' : ''}
                      onChange={e => {
                        const val = e.target.value === 'yes';
                        set('has_allergies', val);
                        if (!val) set('allergy_details', '');
                      }}
                    >
                      <FormControlLabel value="yes" control={<Radio size="small" />} label={t('academy.students.form.yes')} />
                      <FormControlLabel value="no" control={<Radio size="small" />} label={t('academy.students.form.no')} />
                    </RadioGroup>
                    {formData.has_allergies && (
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        sx={{ mt: 1.5 }}
                        label={t('academy.students.form.allergy_details')}
                        placeholder={t('academy.students.form.allergy_details_placeholder')}
                        value={formData.allergy_details ?? ''}
                        onChange={e => set('allergy_details', e.target.value)}
                      />
                    )}
                  </FormControl>
                </Paper>
              </Grid>

              {/* Image right */}
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default' }}>
                  <FormControl component="fieldset">
                    <FormLabel sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 1 }}>{t('academy.students.form.image_right')}</FormLabel>
                    <RadioGroup
                      row
                      value={formData.image_right === true ? 'yes' : formData.image_right === false ? 'no' : ''}
                      onChange={e => set('image_right', e.target.value === 'yes')}
                    >
                      <FormControlLabel value="yes" control={<Radio size="small" />} label={t('academy.students.form.yes')} />
                      <FormControlLabel value="no" control={<Radio size="small" />} label={t('academy.students.form.no')} />
                    </RadioGroup>
                  </FormControl>
                </Paper>
              </Grid>

              {/* Guardian accordion */}
              <Grid item xs={12}>
                <Accordion defaultExpanded variant="outlined" sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'action.hover', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FamilyIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle2" fontWeight={700}>{t('academy.students.form.guardian_title')}</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          label={t('academy.students.form.guardian_firstname')} size="small" fullWidth
                          value={formData.guardian_first_name ?? ''}
                          onChange={e => set('guardian_first_name', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          label={t('academy.students.form.guardian_lastname')} size="small" fullWidth
                          value={formData.guardian_last_name ?? ''}
                          onChange={e => set('guardian_last_name', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          select label={t('academy.students.form.guardian_relation')} size="small" fullWidth
                          value={formData.guardian_relation ?? ''}
                          onChange={e => set('guardian_relation', e.target.value as GuardianRelation)}
                        >
                          {GUARDIAN_RELATIONS.map(r => <MenuItem key={r} value={r}>{t(GUARDIAN_RELATION_KEYS[r])}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          label={t('academy.students.form.guardian_phone')} size="small" fullWidth
                          value={formData.guardian_phone ?? ''}
                          onChange={e => set('guardian_phone', e.target.value)}
                          InputProps={{
                            startAdornment: <ContactPhoneIcon sx={{ color: 'action.active', mr: 1, fontSize: 18 }} />,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label={t('academy.students.form.guardian_email')} type="email" size="small" fullWidth
                          value={formData.guardian_email ?? ''}
                          onChange={e => set('guardian_email', e.target.value)}
                          placeholder="parent@email.com"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label={t('academy.students.form.authorized_pickup')}
                          size="small" fullWidth multiline rows={2}
                          placeholder={t('academy.students.form.authorized_pickup_placeholder')}
                          value={formData.authorized_pickup ?? ''}
                          onChange={e => set('authorized_pickup', e.target.value)}
                          helperText={t('academy.students.form.authorized_pickup_hint')}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

            </Grid>
          </Paper>
        )}

        {/* ── Portal Access Section ── */}
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            borderRadius: 2,
            mt: 3,
            borderColor: formData.portal_active ? 'info.main' : 'divider',
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(6, 182, 212, 0.04)' : 'rgba(6, 182, 212, 0.02)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} color="info.main" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
              {t('academy.students.portal_section')}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="info"
              startIcon={<LinkIcon />}
              onClick={() => {
                const url = `${window.location.origin}/portal/student`;
                navigator.clipboard.writeText(url);
                blobticMessage.success(t('academy.students.portal_link_copied'));
              }}
              sx={{ textTransform: 'none', borderRadius: 1.5 }}
            >
              {t('academy.students.portal_copy_link')}
            </Button>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(formData.portal_active)}
                    onChange={(e) => set('portal_active', e.target.checked)}
                    color="info"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={600}>
                    {t('academy.students.portal_active')}
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  label={t('academy.students.portal_password')}
                  size="small"
                  type="text"
                  fullWidth
                  disabled={!formData.portal_active}
                  value={formData.portal_password ?? ''}
                  onChange={(e) => set('portal_password', e.target.value)}
                  placeholder="Ex: Etudiant2026*"
                  helperText={t('academy.students.portal_login_hint')}
                  sx={fsx}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!formData.portal_active}
                  onClick={() => {
                    const rnd = 'Std_' + Math.random().toString(36).slice(-6) + '!';
                    set('portal_password', rnd);
                  }}
                  sx={{ whiteSpace: 'nowrap', minWidth: 90, height: 40, mt: -2.5, textTransform: 'none' }}
                >
                  {t('academy.students.portal_generate_pwd')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* ── Dossier Information ── */}
        {(formData.dos_code || formData.dos_lib) && (
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mt: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
              {t('academy.students.section_dossier')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('academy.students.form.dos_code')}
                  size="small" fullWidth disabled
                  value={formData.dos_code ?? ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('academy.students.form.dos_lib')}
                  size="small" fullWidth disabled
                  value={formData.dos_lib ?? ''}
                />
              </Grid>
            </Grid>
          </Paper>
        )}

      </Box>

      <PublicPreRegistrationModal
        open={publicModalOpen}
        onClose={() => setPublicModalOpen(false)}
      />
    </GenericPage>
  );
};

export default StudentsPage;
